import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Cake customization details
export interface CakeDetails {
  size: string;
  flavor: string;
  filling: string;
  occasion: string;
  cakeMessage: string;
  designNotes: string;
  servings: string;
  dietaryNotes: string;
}

// Generic product customizations format (from ProductCustomization component)
export interface ProductCustomizations {
  [groupId: string]: {
    groupName: string;
    selections: {
      id: string;
      name: string;
      price: number;
    }[];
  };
}

// Cart item type
export interface CartItem {
  /** Stable id for this cart line (used for remove/update/edit) */
  lineId: string;
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  notes?: string;
  requiresApproval?: boolean; // For custom cakes
  customizations?: ProductCustomizations; // For customizable products (sandwiches, etc.)
  /** Menu category - used for tax rules when tax_applies is not set */
  category?: string;
  /** Per-item HST override from menu: true = always taxable, false = never, null/undefined = use category rules */
  tax_applies?: boolean | null;
}

// Cart context type
interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity' | 'lineId'>, quantity?: number) => void;
  removeItem: (lineId: string) => void;
  updateQuantity: (lineId: string, quantity: number) => void;
  updateItemNotes: (lineId: string, notes: string) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  isCartOpen: boolean;
  setCartOpen: (open: boolean) => void;
  toggleCart: () => void;
  // Cake-specific
  cakeDetails: CakeDetails | null;
  setCakeDetails: (details: CakeDetails | null) => void;
  hasCakeItems: boolean; // True if any item requires approval
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCartOpen, setCartOpen] = useState(false);
  const [cakeDetails, setCakeDetails] = useState<CakeDetails | null>(null);

  const toggleCart = () => setCartOpen(prev => !prev);

  // Generate a stable line id for a new cart line
  const generateLineId = () =>
    `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  // Ensure every cart item has a lineId (fixes remove/update for legacy items or edge cases)
  useEffect(() => {
    const needsMigration = items.some((i) => !i.lineId || i.lineId === '');
    if (!needsMigration || items.length === 0) return;
    setItems((current) =>
      current.map((i) =>
        i.lineId && i.lineId !== '' ? i : { ...i, lineId: generateLineId() }
      )
    );
  }, [items.length]);

  // Add item to cart
  // Items with different customizations are treated as separate items
  const addItem = (item: Omit<CartItem, 'quantity' | 'lineId'>, quantity = 1) => {
    setItems(currentItems => {
      // For customizable items, compare both id AND customizations
      // Two items with same id but different customizations are different cart items
      const customizationKey = item.customizations
        ? JSON.stringify(item.customizations)
        : null;

      const existingItem = currentItems.find(i => {
        if (i.id !== item.id) return false;
        const existingKey = i.customizations
          ? JSON.stringify(i.customizations)
          : null;
        return customizationKey === existingKey;
      });

      if (existingItem) {
        return currentItems.map(i => {
          if (i.id !== item.id) return i;
          const existingKey = i.customizations
            ? JSON.stringify(i.customizations)
            : null;
          if (customizationKey === existingKey) {
            return { ...i, quantity: i.quantity + quantity };
          }
          return i;
        });
      }

      // Add new item with a unique lineId
      return [...currentItems, { ...item, quantity, lineId: generateLineId() }];
    });
  };

  // Remove item from cart by lineId
  const removeItem = (lineId: string) => {
    setItems(currentItems => {
      const newItems = currentItems.filter(item => item.lineId !== lineId);
      const stillHasCakes = newItems.some(item => item.requiresApproval);
      if (!stillHasCakes) setCakeDetails(null);
      return newItems;
    });
  };

  // Update item quantity by lineId
  const updateQuantity = (lineId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(lineId);
      return;
    }
    setItems(currentItems =>
      currentItems.map(item =>
        item.lineId === lineId ? { ...item, quantity } : item
      )
    );
  };

  // Update notes for a cart line (e.g. stuffed cookie flavours)
  const updateItemNotes = (lineId: string, notes: string) => {
    setItems(currentItems =>
      currentItems.map(item =>
        item.lineId === lineId ? { ...item, notes } : item
      )
    );
  };

  // Clear all items
  const clearCart = () => {
    setItems([]);
    setCakeDetails(null);
  };

  // Calculate totals
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  // Check if cart has any items requiring approval (custom cakes)
  const hasCakeItems = items.some(item => item.requiresApproval);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        updateItemNotes,
        clearCart,
        totalItems,
        totalPrice,
        isCartOpen,
        setCartOpen,
        toggleCart,
        cakeDetails,
        setCakeDetails,
        hasCakeItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

// Hook to use cart
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

